/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { UseQueryResult } from '@tanstack/react-query';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import type { FieldValues, UseFormSetValue } from 'react-hook-form';
import type { UseMutationReturnType } from '@opengeoweb/api';
import { useAuthenticationContext, useAuthQuery } from '@opengeoweb/api';
import {
  getBulletin,
  getBulletinHistoryList,
  getEvent,
  getEventList,
  getNewNotifications,
  getStreams,
  getTimeSeries,
  postDiscardDraftNotification,
  postNotification,
  postPopulateTemplateContent,
  postSetAcknowledged,
} from './api';
import type {
  Bulletin,
  DiscardDraftNotification,
  EventCategoryParams,
  StreamResponse,
  SWEvent,
  SWNotification,
  TimeseriesParams,
  TimeseriesResponseData,
} from '../../types';

// Bulletin section
export const useBulletin = (): UseQueryResult<Bulletin> =>
  useAuthQuery(['bulletin'], getBulletin, false, 5 * 60 * 1000);

export const useBulletinHistory = (): UseQueryResult<Bulletin[]> =>
  useAuthQuery(['bulletin-history-list'], getBulletinHistoryList, false);

// Notification section
export const useEventList = (
  params: EventCategoryParams,
): UseQueryResult<SWEvent[]> => {
  const { auth } = useAuthenticationContext();
  return useQuery({
    queryKey: ['event-list', params],
    queryFn: async (): Promise<SWEvent[]> => getEventList(auth!, params),
    enabled: !!auth,
    refetchInterval: 5 * 60 * 1000,
  });
};

export const useEvent = (eventid: string): UseQueryResult<SWEvent> => {
  const { auth } = useAuthenticationContext();
  return useQuery({
    queryKey: ['event', eventid],
    queryFn: async (): Promise<SWEvent> => getEvent(auth!, eventid),
    enabled: !!auth,
  });
};

export const useNewNotifications = (): UseQueryResult<SWEvent[]> =>
  useAuthQuery(
    ['new-notifications'],
    getNewNotifications,
    false,
    1 * 60 * 1000,
  );

interface BasePostNotificationProps {
  setError: (value: string) => void;
}

interface PostNotificationProps extends BasePostNotificationProps {
  data: SWNotification;
  toggleDialogOpen: (formSaved?: boolean) => void;
}
export const usePostNotification = (): UseMutationReturnType<
  void,
  PostNotificationProps
> => {
  const queryClient = useQueryClient();
  const { auth } = useAuthenticationContext();

  return useMutation({
    mutationFn: async ({ data, setError }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      setError('');
      return postNotification(auth, data);
    },
    onSuccess: async (_data, { data: notification, toggleDialogOpen }) => {
      toggleDialogOpen(true);
      await queryClient.invalidateQueries({
        queryKey: ['event', notification.eventid],
      });
      // trigger refetch for the list
      await queryClient.invalidateQueries({
        queryKey: ['event-list'],
      });
    },
    onError: (error, { setError }) => {
      setError(error.message);
    },
  });
};

interface PostDiscardDraftNotificationProps extends BasePostNotificationProps {
  data: DiscardDraftNotification;
  toggleDialogOpen: (formSaved?: boolean) => void;
}
export const useDiscardDraftNotification = (): UseMutationReturnType<
  void,
  PostDiscardDraftNotificationProps
> => {
  const queryClient = useQueryClient();
  const { auth } = useAuthenticationContext();

  return useMutation({
    mutationFn: async ({ data, setError }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      setError('');
      return postDiscardDraftNotification(auth, data);
    },
    onSuccess: async (_data, { toggleDialogOpen }) => {
      toggleDialogOpen(true);
      // trigger refetch for the list
      await queryClient.invalidateQueries({
        queryKey: ['event-list'],
      });
    },
    onError: (error, { setError }) => {
      setError(error.message);
    },
  });
};

interface PostSetAcknowledgedProps {
  data: string;
}

export const useSetAcknowledged = (): UseMutationReturnType<
  void,
  PostSetAcknowledgedProps
> => {
  const queryClient = useQueryClient();
  const { auth } = useAuthenticationContext();

  return useMutation({
    mutationFn: async ({ data }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      return postSetAcknowledged(auth, data);
    },
    onSuccess: async () => {
      // trigger refetch for the list
      await queryClient.invalidateQueries({
        queryKey: ['new-notifications'],
      });
    },
  });
};

interface PostPopulateTemplateContentProps extends BasePostNotificationProps {
  data: SWNotification;
  setValue: UseFormSetValue<FieldValues>;
}

export const usePopulateTemplateContent = (): UseMutationReturnType<
  { title: string; message: string },
  PostPopulateTemplateContentProps
> => {
  const { auth } = useAuthenticationContext();

  return useMutation({
    mutationFn: async ({ data, setError }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      setError('');
      return postPopulateTemplateContent(auth, data);
    },
    onSuccess: (data, { setValue }) => {
      setValue('title', data.title);
      setValue('message', data.message);
    },
    onError: (error, { setError }) => {
      setError(error.message);
    },
  });
};

export const useGetStreams = (
  isDisabled = false,
): UseQueryResult<StreamResponse[]> =>
  useAuthQuery(['stream-auto-update'], getStreams, isDisabled, 1 * 60 * 1000);

export const useGetTimeSeries = (
  options: TimeseriesParams[],
): UseQueryResult<TimeseriesResponseData>[] => {
  const { auth } = useAuthenticationContext();

  const queries = useMemo(
    () =>
      options.map((option) => ({
        queryKey: ['timeseries', option.parameter, option.time_stop],
        queryFn: async (): Promise<TimeseriesResponseData> =>
          getTimeSeries(auth!, option),
        enabled: !!auth && !!option,
      })),
    [auth, options],
  );

  return useQueries({
    queries,
  });
};
